import { Component, Renderer2 } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AuthService } from './services/auth.service';
import { CreditsService, CreditBalance } from './services/credits.service';
import { LoginComponent } from './components/login/login';
import { DialogHostComponent } from './components/shared/dialog-host/dialog-host.component';
import { GenerationToastComponent } from './components/shared/generation-toast/generation-toast.component';
import { DialogService } from './services/dialog.service';
import { ManagedAccount, User } from './models/user.model';
import { formatCredits } from './shared/credit-format';

@Component({
    selector: 'app-root',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        LoginComponent,
        DialogHostComponent,
        GenerationToastComponent,
        RouterLink,
        RouterLinkActive,
        RouterOutlet
    ],
    templateUrl: './app.component.html',
    styleUrl: './app.component.css'
})
export class AppComponent {
    title = 'social-calendar';
    isEditorRoute = false;
    isAdminRoute = false;
    sidebarOpen = false;

    isAuthenticated = false;
    isSuperAdmin = false;
    hasWhatsappModule = false;
    hasEmailModule = false;
    hasInsightsModule = false;
    hasTrendsModule = false;
    hasCopywritingModule = false;
    hasLeadGenerationModule = false;
    currentUser: User | null = null;
    managedAccounts: ManagedAccount[] = [];
    activeWorkspaceId: string | null = null;
    creditBalance: CreditBalance | null = null;
    creditsLoading = false;

    constructor(
        public authService: AuthService,
        private creditsService: CreditsService,
        private router: Router,
        private dialogService: DialogService,
        private renderer: Renderer2
    ) {
        this.isEditorRoute = this.router.url.startsWith('/editor/');
        this.isAdminRoute = this.router.url.startsWith('/admin');

        window.alert = (message?: any) => {
            void this.dialogService.alert(String(message ?? ''));
        };

        this.router.events.pipe(
            filter(e => e instanceof NavigationEnd)
        ).subscribe((e: any) => {
            this.isEditorRoute = e.url.startsWith('/editor/');
            this.isAdminRoute = e.url.startsWith('/admin');
            this.sidebarOpen = false;
            this.renderer.removeClass(document.body, 'sidebar-open');
        });

        this.authService.currentUser$.subscribe(user => {
            this.currentUser = user;
            this.isAuthenticated = !!user;
            this.isSuperAdmin = user?.role === 'SUPER_ADMIN';
            this.hasWhatsappModule = this.authService.hasModule('WHATSAPP');
            this.hasEmailModule = this.authService.hasModule('EMAIL');
            this.hasInsightsModule = this.authService.hasModule('INSIGHTS');
            this.hasTrendsModule = this.authService.hasModule('TRENDS');
            this.hasCopywritingModule = this.authService.hasModule('COPYWRITING');
            this.hasLeadGenerationModule = this.authService.hasModule('LEAD_GENERATION');
            this.managedAccounts = user?.managedAccounts || [];
            if (this.isSuperAdmin && !this.isAdminRoute) {
                this.router.navigateByUrl('/admin/dashboard');
                return;
            }
            if (!this.isSuperAdmin && this.isAdminRoute) {
                this.router.navigateByUrl('/ads-campaigns');
                return;
            }
            this.refreshCredits();
        });

        this.authService.activeWorkspace$.subscribe(workspaceId => {
            this.activeWorkspaceId = workspaceId;
            this.refreshCredits();
        });

        this.creditsService.walletRefresh$.subscribe(() => {
            this.refreshCredits();
        });
    }

    toggleSidebar(): void {
        this.sidebarOpen = !this.sidebarOpen;
        if (this.sidebarOpen) {
            this.renderer.addClass(document.body, 'sidebar-open');
        } else {
            this.renderer.removeClass(document.body, 'sidebar-open');
        }
    }

    closeSidebar(): void {
        this.sidebarOpen = false;
        this.renderer.removeClass(document.body, 'sidebar-open');
    }

    logout(): void {
        this.authService.logout();
        this.router.navigateByUrl('/');
    }

    get canManageClients(): boolean {
        return !!this.currentUser?.canManageClients;
    }

    get selectedWorkspaceValue(): string {
        return this.activeWorkspaceId || 'self';
    }

    get activeWorkspaceLabel(): string {
        if (!this.activeWorkspaceId) {
            return this.currentUser?.name || this.currentUser?.username || 'Workspace azienda';
        }

        const activeAccount = this.managedAccounts.find(account => account.id === this.activeWorkspaceId);
        return activeAccount?.name || activeAccount?.username || 'Workspace cliente';
    }

    isWorkspaceActive(accountId: string | null): boolean {
        return (accountId || null) === (this.activeWorkspaceId || null);
    }

    onWorkspaceChange(value: string): void {
        this.authService.switchWorkspace(value === 'self' ? null : value);
        this.router.navigateByUrl('/');
    }

    get isLowCredits(): boolean {
        return Number(this.creditBalance?.balanceCredits || 0) > 0 && Number(this.creditBalance?.balanceCredits || 0) < 5000;
    }

    formatCredits(value: number | null | undefined): string {
        return formatCredits(value);
    }

    private refreshCredits(): void {
        if (!this.isAuthenticated || this.isSuperAdmin) {
            this.creditBalance = null;
            return;
        }
        this.creditsLoading = true;
        this.creditsService.getBalance().subscribe({
            next: (balance) => {
                this.creditBalance = balance;
                this.creditsLoading = false;
            },
            error: () => {
                this.creditBalance = null;
                this.creditsLoading = false;
            }
        });
    }
}
